import { useRef, useEffect, useCallback, useState } from "react";
import type { MarketWithHistory } from "../types";

interface Props {
  markets: MarketWithHistory[];
}

const PALETTE = [
  "#ff6b6b",
  "#ff9f43",
  "#feca57",
  "#48dbfb",
  "#0abde3",
  "#5f27cd",
  "#ee5a24",
  "#6ab04c",
  "#e056fd",
  "#22a6b3",
  "#f9ca24",
  "#eb4d4b",
  "#7ed6df",
  "#badc58",
  "#f0932b",
  "#6c5ce7",
  "#fdcb6e",
  "#e17055",
  "#00cec9",
  "#a29bfe",
];

// True isometric: 30° from horizontal
const COS30 = Math.cos(Math.PI / 6); // ≈ 0.866
const SIN30 = Math.sin(Math.PI / 6); // = 0.5

function formatDate(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function shortLabel(question: string, allQuestions: string[]): string {
  if (allQuestions.length < 2) return question.replace(/\?$/, "").trim().slice(0, 30);

  const threshold = allQuestions.length * 0.6;

  // Find majority prefix: how many chars most questions share
  let prefix = 0;
  while (prefix < question.length) {
    const ch = question[prefix];
    let count = 0;
    for (const q of allQuestions) {
      if (prefix < q.length && q[prefix] === ch) count++;
    }
    if (count < threshold) break;
    prefix++;
  }

  // Find majority suffix
  let suffix = 0;
  while (suffix < question.length - prefix) {
    const ch = question[question.length - 1 - suffix];
    let count = 0;
    for (const q of allQuestions) {
      if (suffix < q.length - prefix && q[q.length - 1 - suffix] === ch) count++;
    }
    if (count < threshold) break;
    suffix++;
  }

  const unique = question.slice(prefix, question.length - suffix).trim();
  return unique || question.replace(/\?$/, "").trim().slice(0, 30);
}

export function Chart({ markets }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    market: string;
    date: string;
    price: string;
    color: string;
  } | null>(null);

  const layoutRef = useRef<{
    layers: Array<{
      marketIdx: number;
      points: Array<{ sx: number; sy: number; t: number; p: number }>;
    }>;
  }>({ layers: [] });

  const allQuestions = markets.map((m) => m.info.question);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || markets.length === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    const W = rect.width;
    const H = rect.height;

    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;

    const ctx = canvas.getContext("2d")!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    const N = markets.length;
    const margin = { top: 30, right: 40, bottom: 60, left: 50 };
    const availW = W - margin.left - margin.right;
    const availH = H - margin.top - margin.bottom;

    // Isometric projection (rotated 90° CCW around Y):
    //   X-axis (time):     30° above horizontal, going right  → screen (+cos30, -sin30)
    //   Y-axis (price):    vertical, going up                 → screen (0, -1)
    //   Z-axis (end date): 30° above horizontal, going left   → screen (-cos30, -sin30)
    //
    // project(xn, yn, zi):
    //   sx = ox + xn * xLen * COS30 - zi * zStep * COS30
    //   sy = oy - xn * xLen * SIN30 - yn * yLen - zi * zStep * SIN30

    // Determine axis lengths to fill available space
    // Bounding box of projection:
    //   width  = (xLen + zLen) * COS30
    //   height = yLen + (xLen + zLen) * SIN30
    //
    // Try filling width first, then check if y-axis has enough room
    let sumXZ = availW / COS30;
    let yLen = availH - sumXZ * SIN30;

    // If y-axis gets squeezed, constrain by height instead
    const minYLen = availH * 0.3;
    if (yLen < minYLen) {
      yLen = minYLen;
      sumXZ = (availH - yLen) / SIN30;
    }

    // Allocate between X and Z: Z gets up to 35% of the total
    const zLen = Math.min(sumXZ * 0.35, (N - 1) * 40);
    const xLen = sumXZ - zLen;
    const zStep = N > 1 ? zLen / (N - 1) : 0;

    // Origin placement:
    // Leftmost:  (x=0, y=0, z=N-1) → sx = ox - zLen*COS30 = margin.left
    // Rightmost: (x=1, y=0, z=0)   → sx = ox + xLen*COS30
    // Bottom:    (x=0, y=0, z=0)   → sy = oy
    // Top:       (x=1, y=1, z=N-1) → sy = oy - yLen - (xLen+zLen)*SIN30 = margin.top
    const totalW = (xLen + zLen) * COS30;
    const hPad = Math.max((availW - totalW) / 2, 0);
    const ox = margin.left + hPad + zLen * COS30;
    const oy = margin.top + yLen + (xLen + zLen) * SIN30;

    const project = (xn: number, yn: number, zi: number) => ({
      sx: ox + xn * xLen * COS30 - zi * zStep * COS30,
      sy: oy - xn * xLen * SIN30 - yn * yLen - zi * zStep * SIN30,
    });

    // Global time range
    let tMin = Infinity, tMax = -Infinity;
    for (const m of markets) {
      for (const pt of m.history) {
        if (pt.t < tMin) tMin = pt.t;
        if (pt.t > tMax) tMax = pt.t;
      }
    }
    if (tMin === tMax) tMax = tMin + 1;

    // Backfill
    const backfilled: Array<{ t: number; p: number }[]> = markets.map((m) => {
      if (m.history.length === 0) return m.history;
      if (m.history[0]!.t <= tMin) return m.history;
      return [{ t: tMin, p: 0 }, { t: m.history[0]!.t - 1, p: 0 }, ...m.history];
    });

    const xNorm = (t: number) => (t - tMin) / (tMax - tMin);

    // --- Draw axes and grid ---

    // Front face grid lines (z=0): horizontal price ticks
    ctx.strokeStyle = "rgba(255,255,255,0.08)";
    ctx.lineWidth = 1;
    for (let p = 0; p <= 1; p += 0.1) {
      const a = project(0, p, 0);
      const b = project(1, p, 0);
      ctx.beginPath();
      ctx.moveTo(a.sx, a.sy);
      ctx.lineTo(b.sx, b.sy);
      ctx.stroke();
    }

    // Floor grid lines (y=0): time ticks extending along z
    const timeSteps = Math.min(8, Math.floor(xLen * COS30 / 70));
    for (let i = 0; i <= timeSteps; i++) {
      const xn = i / timeSteps;
      const a = project(xn, 0, 0);
      const b = project(xn, 0, N - 1);
      ctx.strokeStyle = "rgba(255,255,255,0.05)";
      ctx.beginPath();
      ctx.moveTo(a.sx, a.sy);
      ctx.lineTo(b.sx, b.sy);
      ctx.stroke();
    }

    // Z-axis lines on floor at x=0 and x=1
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    for (const xn of [0, 1]) {
      const a = project(xn, 0, 0);
      const b = project(xn, 0, N - 1);
      ctx.beginPath();
      ctx.moveTo(a.sx, a.sy);
      ctx.lineTo(b.sx, b.sy);
      ctx.stroke();
    }

    // X & Z axis lines (behind layers)
    ctx.strokeStyle = "rgba(255,255,255,0.2)";
    ctx.lineWidth = 1;
    const yBot = project(0, 0, 0);
    // X-axis (front floor)
    const xEnd = project(1, 0, 0);
    ctx.beginPath();
    ctx.moveTo(yBot.sx, yBot.sy);
    ctx.lineTo(xEnd.sx, xEnd.sy);
    ctx.stroke();
    // Z-axis (left floor)
    const zEnd = project(0, 0, N - 1);
    ctx.beginPath();
    ctx.moveTo(yBot.sx, yBot.sy);
    ctx.lineTo(zEnd.sx, zEnd.sy);
    ctx.stroke();

    // X-axis labels (along front floor, x goes upper-right)
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    for (let i = 0; i <= timeSteps; i++) {
      const xn = i / timeSteps;
      const t = tMin + xn * (tMax - tMin);
      const pt = project(xn, 0, 0);
      ctx.save();
      ctx.translate(pt.sx, pt.sy + 6);
      ctx.rotate(-Math.PI / 6); // rotate label to follow the x-axis angle (30° upward)
      ctx.fillText(formatDate(t), 0, 0);
      ctx.restore();
    }

    // --- Draw market layers back-to-front ---
    const layers: typeof layoutRef.current.layers = [];

    for (let zi = N - 1; zi >= 0; zi--) {
      const hist = backfilled[zi]!;
      const color = PALETTE[zi % PALETTE.length]!;
      if (hist.length === 0) continue;

      // Compute screen points
      const points: Array<{ sx: number; sy: number; t: number; p: number }> = [];
      for (const pt of hist) {
        const { sx, sy } = project(xNorm(pt.t), pt.p, zi);
        points.push({ sx, sy, t: pt.t, p: pt.p });
      }

      // Floor baseline for this layer
      const floorStart = project(xNorm(hist[0]!.t), 0, zi);
      const floorEnd = project(xNorm(hist.at(-1)!.t), 0, zi);

      // Occlusion fill (line → floor)
      ctx.beginPath();
      ctx.moveTo(floorStart.sx, floorStart.sy);
      for (const pt of points) ctx.lineTo(pt.sx, pt.sy);
      ctx.lineTo(floorEnd.sx, floorEnd.sy);
      ctx.closePath();
      ctx.fillStyle = "rgba(13, 13, 20, 0.88)";
      ctx.fill();

      // Subtle color fill
      ctx.beginPath();
      ctx.moveTo(floorStart.sx, floorStart.sy);
      for (const pt of points) ctx.lineTo(pt.sx, pt.sy);
      ctx.lineTo(floorEnd.sx, floorEnd.sy);
      ctx.closePath();
      ctx.fillStyle = color + "18";
      ctx.fill();

      // The line
      ctx.beginPath();
      ctx.moveTo(points[0]!.sx, points[0]!.sy);
      for (let j = 1; j < points.length; j++) ctx.lineTo(points[j]!.sx, points[j]!.sy);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Label at floor level (y=0) at the start of this layer
      const floorPt = project(0, 0, zi);
      ctx.save();
      ctx.translate(floorPt.sx - 6, floorPt.sy);
      ctx.rotate(-Math.PI / 6);
      ctx.fillStyle = color;
      ctx.font = "bold 10px sans-serif";
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText(shortLabel(markets[zi]!.info.question, allQuestions), 0, 0);
      ctx.restore();

      layers.push({ marketIdx: zi, points });
    }

    // --- Y-axis drawn last (in front of all layers) ---
    ctx.strokeStyle = "rgba(255,255,255,0.3)";
    ctx.lineWidth = 1;
    const yTop = project(0, 1, 0);
    ctx.beginPath();
    ctx.moveTo(yBot.sx, yBot.sy);
    ctx.lineTo(yTop.sx, yTop.sy);
    ctx.stroke();

    // Y-axis labels
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.font = "11px monospace";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    for (let p = 0; p <= 1; p += 0.2) {
      const pt = project(0, p, 0);
      ctx.fillText(`${Math.round(p * 100)}%`, pt.sx - 10, pt.sy);
    }

    layoutRef.current = { layers };
  }, [markets]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => draw());
    observer.observe(container);
    draw();
    return () => observer.disconnect();
  }, [draw]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      let bestDist = 18;
      let best: { market: string; date: string; price: string; x: number; y: number; color: string } | null = null;

      for (const layer of layoutRef.current.layers) {
        const m = markets[layer.marketIdx]!;
        const color = PALETTE[layer.marketIdx % PALETTE.length]!;
        for (const pt of layer.points) {
          const dist = Math.hypot(pt.sx - mx, pt.sy - my);
          if (dist < bestDist) {
            bestDist = dist;
            best = {
              market: shortLabel(m.info.question, allQuestions),
              date: formatDate(pt.t),
              price: `${(pt.p * 100).toFixed(1)}%`,
              x: pt.sx,
              y: pt.sy,
              color,
            };
          }
        }
      }

      setTooltip(best ? { x: best.x, y: best.y, market: best.market, date: best.date, price: best.price, color: best.color } : null);
    },
    [markets]
  );

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  useEffect(() => {
    draw();
    if (!tooltip) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d")!;
    // Transform already set by draw() — don't scale again

    // Dot
    ctx.beginPath();
    ctx.arc(tooltip.x, tooltip.y, 4, 0, Math.PI * 2);
    ctx.fillStyle = tooltip.color;
    ctx.fill();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }, [tooltip, draw]);

  return (
    <div ref={containerRef} style={{ flex: 1, position: "relative" }}>
      <canvas
        ref={canvasRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ display: "block", width: "100%", height: "100%" }}
      />
      {tooltip && (
        <div
          className="chart-tooltip"
          style={{
            left: tooltip.x + 14,
            top: tooltip.y - 44,
          }}
        >
          <div className="tooltip-market" style={{ color: tooltip.color }}>{tooltip.market}</div>
          <div>
            {tooltip.date} &mdash; <strong>{tooltip.price}</strong>
          </div>
        </div>
      )}
    </div>
  );
}

export function Legend({ markets }: Props) {
  return (
    <div className="legend">
      {markets.map((m, i) => (
        <div key={m.info.conditionId} className="legend-item">
          <span className="legend-color" style={{ background: PALETTE[i % PALETTE.length] }} />
          <span className="legend-label">
            {shortLabel(m.info.question, markets.map((m) => m.info.question))}
          </span>
        </div>
      ))}
    </div>
  );
}
